import { resolveTemplateFieldValues } from './templateFieldResolver';
import { formatPhone } from './phone';

const COMMON_VARIABLE_LABELS = [
    'Time of day',
    'Customer name',
    'Gate',
    'Driver',
    'Driver phone',
    'Vehicle',
];

function firstString(...values) {
    for (const value of values) {
        if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
}

export function getTemplateBody(template) {
    return firstString(template?.body, template?.types?.['twilio/text']?.body);
}

export function getTemplateDisplayName(template) {
    const displayName = firstString(template?.display_name, template?.displayName);
    if (displayName) return displayName;
    const systemName = firstString(template?.name, template?.sid);
    return systemName
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function getTemplateVariables(template) {
    const body = getTemplateBody(template);
    const matches = [...body.matchAll(/\{\{\s*([^{}]+?)\s*\}\}/g)].map((match) => match[1]);
    const keys = [...new Set([...matches, ...Object.keys(template?.variables || {})])];
    return Object.fromEntries(keys.map((key) => [key, '']));
}

export function getTemplateVariableLabel(template, key, index = 0) {
    const explicit = firstString(template?.variableLabels?.[key], template?.variable_labels?.[key]);
    if (explicit) return explicit;
    if (!/^\d+$/.test(String(key))) {
        // Sentence case ("time_of_day" -> "Time of day"); the key itself is unchanged.
        const words = String(key).replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
        return words.charAt(0).toUpperCase() + words.slice(1);
    }
    return COMMON_VARIABLE_LABELS[index] || `Template value ${key}`;
}

// The template's own sample value for a variable (Twilio Content keeps samples as plain strings; templates with source
// metadata keep { sample, source }), or ''.
export function getTemplateVariableSample(template, key) {
    const entry = template?.variables?.[key];
    if (typeof entry === 'string') return entry.trim();
    if (entry && typeof entry === 'object' && typeof entry.sample === 'string') return entry.sample.trim();
    return '';
}

// Values for a template's variables: what the conversation/contact/trip already knows, else the template's sample value,
// else empty. A brand-new recipient's placeholder name ("New contact (...)") is never used as a client name.
export function buildTemplateVariableValues(template, context = {}, { useSampleFallback = true } = {}) {
    const values = buildContextTemplateVariableValues(template, context);
    return Object.fromEntries(Object.entries(values).map(([key, value]) => [
        key,
        typeof value === 'string' && value.trim()
            ? value
            : (useSampleFallback ? getTemplateVariableSample(template, key) : ''),
    ]));
}

function buildContextTemplateVariableValues(template, context = {}) {
    const {
        contact = null,
        trip = null,
        session = null,
        contactName = '',
        engagement = null,
        flight = null,
        assignment = null,
        driver = null,
        vehicle = null,
        // Comms gate fixes: no Eastern default here; the resolver picks the zone (explicit, conversation, company, then its
        // named "company time zone unknown" fallback), so an explicit 'America/New_York' is honoured like any other zone.
        timezone = '',
        staffName = '',
        companyName = '',
        greetingCutoffs = null,
        now = null,
    } = context || {};
    const keys = Object.keys(getTemplateVariables(template));
    const hasSourceMetadata = keys.some((key) => (
        template?.variables?.[key]
        && typeof template.variables[key] === 'object'
        && Object.prototype.hasOwnProperty.call(template.variables[key], 'source')
    ));
    if (hasSourceMetadata) {
        return resolveTemplateFieldValues(template?.variables || {}, {
            session: session || trip,
            contact,
            contactName,
            trip,
            engagement,
            flight,
            assignment,
            driver,
            vehicle,
            timezone,
            staffName,
            companyName,
            greetingCutoffs,
            now,
        }).values;
    }
    return Object.fromEntries(keys.map((key, index) => {
        const label = getTemplateVariableLabel(template, key, index).toLowerCase();
        const keyName = String(key).toLowerCase();
        const is = (...terms) => terms.some((term) => label.includes(term) || keyName.includes(term));
        const value = is('customer', 'client', 'contact', 'name')
            ? (contact?.isNewRecipient ? '' : contact?.name)
            : is('gate')
                ? trip?.gate || trip?.pickup_gate
                : is('driver phone', 'driver number')
                    ? formatPhone(trip?.driver_phone || trip?.driverPhone || '', 'US')
                    : is('driver')
                        ? trip?.driver_name || trip?.driverName
                        : is('vehicle')
                            ? trip?.vehicle || trip?.vehicle_description
                            : is('time', 'pickup')
                                ? trip?.time || trip?.pickup_time || trip?.scheduled_at
                                : '';
        return [key, typeof value === 'string' ? value : ''];
    }));
}

export function getAllowedMessageTypes({ channel, knownRecipient = true, withinWhatsAppWindow = false, recipientCount = 0 } = {}) {
    const types = [];
    if (channel !== 'whatsapp' || (knownRecipient && withinWhatsAppWindow)) types.push('general');
    types.push('template');
    if (Number(recipientCount) >= 2) types.push('campaign');
    return types;
}

export function fillTemplateBody(body, variables = {}) {
    return String(body || '').replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (placeholder, key) => {
        const value = variables[key];
        return typeof value === 'string' || typeof value === 'number' ? String(value) : placeholder;
    });
}

export function getTemplatePreviewSegments(body, variables = {}, template = null) {
    const variableIndices = new Map();
    return String(body || '').split(/(\{\{\s*[^{}]+?\s*\}\})/g).filter(Boolean).map((segment) => {
        const match = segment.match(/^\{\{\s*([^{}]+?)\s*\}\}$/);
        if (!match) return { text: segment, missing: false };
        const key = match[1];
        if (!variableIndices.has(key)) variableIndices.set(key, variableIndices.size);
        const value = variables[key];
        return {
            text: typeof value === 'string' && value.trim() ? value : getTemplateVariableLabel(template, key, variableIndices.get(key)),
            missing: !(typeof value === 'string' && value.trim()),
        };
    });
}

export function sortTemplatesRecentFirst(templates = [], recentSids = []) {
    const positions = new Map(recentSids.map((sid, index) => [sid, index]));
    return [...templates].sort((a, b) => {
        const aPosition = positions.has(a.sid) ? positions.get(a.sid) : Number.MAX_SAFE_INTEGER;
        const bPosition = positions.has(b.sid) ? positions.get(b.sid) : Number.MAX_SAFE_INTEGER;
        return aPosition - bPosition || String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
    });
}

export function readRecentTemplateSids(storageKey) {
    if (!storageKey || typeof window === 'undefined') return [];
    try {
        const parsed = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
        return Array.isArray(parsed) ? parsed.filter((sid) => typeof sid === 'string') : [];
    } catch {
        return [];
    }
}

export function rememberTemplateSid(storageKey, sid) {
    if (!storageKey || !sid || typeof window === 'undefined') return;
    try {
        const next = [sid, ...readRecentTemplateSids(storageKey).filter((current) => current !== sid)].slice(0, 8);
        window.localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
        // Recent-template ordering is a convenience and should not block sending.
    }
}
